import axios from 'axios';
import { store, router } from '@/appContext';
import { pathNames } from '@/constants/routes/acvRoute';
import { decodeJwt } from '@/utilities';
import { getDifferenceInMinutes } from '@/utilities/DateUtils';

const SESSION_EXPIRED_MESSAGE = 'Your session has expired. Please start again.';

/* eslint-disable default-param-last */

const REFRESH_THRESHOLD_MINUTES = 15;
const TOKEN_REFRESH_URL = 'private/v1/token/refresh';

let refreshInFlight = null;

function getTokenExpiration(token) {
  const { exp } = decodeJwt(token) ?? {};
  if (!exp) return null;
  const expiration = new Date(0);
  expiration.setUTCSeconds(exp);
  return expiration;
}

function isCloseToExpiration(token) {
  const expiration = getTokenExpiration(token);
  if (!expiration) return false;
  return getDifferenceInMinutes(expiration, new Date()) < REFRESH_THRESHOLD_MINUTES;
}

/**
 * @class RegistrationClient
 * @description V2 API wrapper for handling registration process, including user and organization.
 */
class RegistrationClient {
  constructor(baseUri = import.meta.env.VITE_ORGANIZATION_REGISTRATION_SERVICE_URI, headers, pathToApiErrors = 'response.data.error.message') {
    this.baseUri = baseUri;
    this.headers = {
      ...headers,
      'Content-type': 'application/json; charset=UTF-8',
      Accept: 'application/json',
    };
    this.axiosInstance = axios.create({
      baseURL: this.baseUri,
      headers: this.headers,
    });
    this.pathToApiErrors = pathToApiErrors;

    this.axiosInstance.interceptors.request.use(async (config) => {
      const { url = '' } = config;
      const { registrationToken } = store.getters;
      const authHeader = config.headers?.Authorization;

      if (url.includes(TOKEN_REFRESH_URL)
        || !registrationToken
        || authHeader !== `Bearer ${registrationToken}`) {
        return config;
      }

      let currentToken = registrationToken;

      if (isCloseToExpiration(currentToken)) {
        // eslint-disable-next-line no-use-before-define -- function declaration is hoisted
        currentToken = await refreshRegistrationTokenIfCurrent();
      }

      return {
        ...config,
        headers: {
          ...config.headers,
          Authorization: `Bearer ${currentToken}`,
        },
      };
    });

    this.axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => RegistrationClient.handleUnauthorized(error),
    );
  }

  static getAuthHeaders() {
    const { registrationToken } = store.getters;
    return registrationToken ? { Authorization: `Bearer ${registrationToken}` } : undefined;
  }

  static handleUnauthorized(axiosError) {
    const error = axiosError;
    const status = error?.response?.status;
    const handleRegistrationExpiry = Boolean(error?.config?.handleRegistrationExpiry);

    if ((status === 401 || status === 403) && handleRegistrationExpiry) {
      store.dispatch('clearRegistrationToken');
      store.dispatch('setSessionExpiredMessage', SESSION_EXPIRED_MESSAGE);
      router.push(pathNames.get('registrationForm_v2')).catch(() => {});
      error.isSessionExpired = true;
    }

    return Promise.reject(error);
  }

  validatePersonAndSendCode({ userAuctionAccessId, last4PhotoId }) {
    return this.axiosInstance.post('public/person/validate', {
      auctionAccessId: String(userAuctionAccessId).toUpperCase(),
      last4PhotoId: String(last4PhotoId).toUpperCase(),
    })
      .then((response) => response)
      .catch((error) => error);
  }

  confirmEmailVerificationCode({ auctionAccessId, code }) {
    return this.axiosInstance.post('public/person/verify', {
      auctionAccessId,
      code,
    }).then((response) => response);
  }

  getPersonDetails({ last4PhotoId }) {
    const headers = RegistrationClient.getAuthHeaders();

    return this.axiosInstance.post('private/v1/person-details', {
      last4PhotoId,
    }, { headers, handleRegistrationExpiry: true }).then((response) => response);
  }

  refreshEmailVerificationSessionToken(token) {
    return this.axiosInstance.post('private/v1/token/refresh', {
      token,
    });
  }

  /**
   * Identifies the user by the `legacyUserId` returned from `createUser` rather than
   * their plaintext credentials, since by this point the account already exists.
   */
  getSteppedUpSessionTokenForNewlyCreatedUser({ organizationAcvId }) {
    const headers = RegistrationClient.getAuthHeaders();
    return this.axiosInstance.post('private/v1/organization-step-up', {
      organizationAcvId,
    }, { headers });
  }

  /**
   * Creates the ACV user account for IO-5446, backed by the User Creation Saga
   * (`CreateUserController` / `api/private/v1/create-user`). Resolves with the response's
   * `token`, which callers pass straight to `setRegistrationToken` — no
   * `getSteppedUpSessionTokenForNewlyCreatedUser` exchange step needed.
   */
  createUser({ emailAddress, password, fullName }) {
    const headers = RegistrationClient.getAuthHeaders();
    return this.axiosInstance.post('/private/v1/create-user', {
      userName: emailAddress,
      password,
      fullName,
    }, { headers, handleRegistrationExpiry: true });
  }

  /**
   * Steps a pre-auth registration-session JWT up to one scoped to a specific dealer, once the
   * user has an authenticated Marketplace session to prove that dealer association.
   */
  getSteppedUpSessionTokenForExistingUser({
    marketplaceJwtToken, steppedDownToken, organizationAcvId,
  }) {
    const headers = {
      Authorization: `Bearer ${marketplaceJwtToken}`,
      ...(steppedDownToken ? { 'ACV-Registration-Authorization': `Bearer ${steppedDownToken}` } : {}),
    };
    return this.axiosInstance.post('private/v1/step-up', {
      organizationAcvId,
    }, { headers })
      .then((response) => response)
      .catch((error) => error);
  }

  registerAAUserAffiliation({ dealerAuctionAccessId, last4PhotoId }) {
    const headers = RegistrationClient.getAuthHeaders();

    return this.axiosInstance.post('private/v1/join', {
      dealerAuctionAccessId: String(dealerAuctionAccessId).toUpperCase(),
      last4PhotoId: String(last4PhotoId).toUpperCase(),
    }, { headers, handleRegistrationExpiry: true });
  }

  registerDealership(payload) {
    const headers = RegistrationClient.getAuthHeaders();
    return this.axiosInstance.post('private/v1/register-dealer', payload, { headers, handleRegistrationExpiry: true });
  }

  /**
   * Submits the Dealership Information step's form. Uses the `private/` prefix since this
   * requires the registration-session JWT (registrationSession Vuex module), not the main
   * app authorization JWT, as this flow can run before a user has an ACV login session.
   */
  registerDealershipInfo(payload) {
    const headers = RegistrationClient.getAuthHeaders();
    return this.axiosInstance.post('private/company/dealer-preferences', payload, { headers, handleRegistrationExpiry: true });
  }

  /**
   * Refreshes the registration-session JWT, added for IO-5375 so a dealer who clicks
   * "Continue Registration" after the inactivity warning gets a fresh token instead of one
   * that's about to expire. Like `submitBankAccount` below, this defines the request contract
   * for a backend endpoint that doesn't exist yet. Authenticates with the current token via the
   * `private/` gateway (same as `registerDealershipInfo`) rather than passing it in the body, and
   * assumes the response returns the new token as `token`, matching
   * `refreshEmailVerificationSessionToken`'s existing parameter naming.
   */
  refreshRegistrationToken() {
    const headers = RegistrationClient.getAuthHeaders();
    return this.axiosInstance.post('private/v1/token/refresh', {}, { headers, handleRegistrationExpiry: true })
      .then((response) => response)
      .catch((error) => error);
  }

  getAllBankAccounts() {
    const headers = RegistrationClient.getAuthHeaders();

    return this.axiosInstance.get('private/v1/bank-accounts', {
      headers,
      handleRegistrationExpiry: true,
    })
      .then((response) => response)
      .catch((error) => error);
  }

  /**
   * Submits a dealer's ACH bank account details, added for IO-5409. This defines the request
   * contract (endpoint path and payload shape) that IO-5410 — the backend ticket adding the
   * real Netsuite-backed handler for this endpoint — will implement server-side; until then,
   * this call has no live endpoint to reach. Uses the `private/` prefix since bank account
   * details should only ever be reachable through the private gateway, not the public one.
   * Resolves with the response on failure rather than throwing (matching `getRegistrationInfo`
   * above), since AchBankAccountForm.vue's call site has no try/catch and only checks
   * `response.status === 200`.
   * `voidedCheck` (added for IO-5411) is sent as `{ fileName, contentType, base64 }` since
   * NetSuite only accepts images/PDFs as a Base64 string within the JSON payload, not
   * multipart/raw binary.
   */
  submitBankAccount({
    depositoryName, accountHolderName, accountNumber, routingNumber, voidedCheck,
  }) {
    const headers = RegistrationClient.getAuthHeaders();

    const payload = {
      depositoryName,
      accountHolderName,
      accountNumber,
      routingNumber,
      voidedCheck,
    };
    return this.axiosInstance.post('private/v1/bank-accounts', payload, { headers, handleRegistrationExpiry: true })
      .then((response) => response)
      .catch((error) => error);
  }

  setPrimaryBankAccount(accountId) {
    const headers = RegistrationClient.getAuthHeaders();

    return this.axiosInstance.post('private/v1/bank-accounts/primary', {
      accountId,
    }, { headers, handleRegistrationExpiry: true })
      .then((response) => response)
      .catch((error) => error);
  }

  getDealerAssignedRep() {
    const headers = RegistrationClient.getAuthHeaders();
    return this.axiosInstance.get('/private/v1/company/dealer-assigned-rep', { headers })
      .then((response) => response)
      .catch((error) => error);
  }

  getDocusignStatus() {
    const headers = RegistrationClient.getAuthHeaders();
    return this.axiosInstance.get('/private/v1/DocuSign/docusign-status', { headers, handleRegistrationExpiry: true })
      .then((response) => response)
      .catch((error) => error);
  }

  checkNeedTosAcceptance() {
    const headers = RegistrationClient.getAuthHeaders();
    return this.axiosInstance.get('private/v1/eula/acceptance-required', { headers, handleRegistrationExpiry: true })
      .then((response) => response.data?.data);
  }

  acceptEula({ eulaId, acceptedDate, organizationId }) {
    const headers = RegistrationClient.getAuthHeaders();
    return this.axiosInstance.post('private/v1/eula-acceptance', {
      eulaId,
      acceptedDate,
      organizationId,
    }, { headers, handleRegistrationExpiry: true }).then((response) => response.data?.data);
  }
}

export function refreshRegistrationTokenIfCurrent() {
  if (refreshInFlight) return refreshInFlight;

  const tokenAtRequestTime = store.getters.registrationToken;

  refreshInFlight = (async () => {
    try {
      const response = await new RegistrationClient().refreshRegistrationToken();
      const newToken = response?.data?.token;
      if (newToken && store.getters.registrationToken === tokenAtRequestTime) {
        await store.dispatch('setRegistrationToken', newToken);
        return newToken;
      }
      return store.getters.registrationToken;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

export default RegistrationClient;
